import {
    Box,
    Pagination,
    PaginationItem,
    Card,
    CardHeader,
    CardContent,
    CardActions,
    Avatar,
    Typography,
} from "@mui/material"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { ListRfidInfo, RfidInfo } from "types/rfid"
import ActiveCardIcon from "assets/svg/ActivateCard.svg"
import DeactiveCardIcon from "assets/svg/DeactivateCard.svg"
import { StatusRfidCell } from "layouts/rfid/rfid-dashboard/cells.tsx"
import { UseRfidContext } from "layouts/rfid/context/provider.tsx"
import { OreveButton } from "components/button/OreveButton.tsx"
import DoDisturbAltOutlinedIcon from "@mui/icons-material/DoDisturbAltOutlined"
import ReplayIcon from "@mui/icons-material/Replay"
import person from "assets/svg/icon-person.svg"
import carUtility from "assets/svg/car-utility.svg"
import RuleIcon from "@mui/icons-material/Rule"
import { useChargingRulesContext } from "layouts/charging-rules/context/useChargingRulesContext.ts"
import { RfidStatus } from "layouts/rfid/utils/const.ts"

export const RfidCard = ({ rows }: { rows: ListRfidInfo }) => {
    const [pageNumber, setPageNumber] = useState(1)
    const [numberOfPages, setNumberOfPages] = useState(0)
    const rowsPerPage = 10
    const {
        setCurrentCardSelected,
        stateDisableCard,
        stateReactivateCard,
        setViewMode,
    } = UseRfidContext()
    const { setRfidCardRuleId } = useChargingRulesContext()

    useEffect(() => {
        setNumberOfPages(Math.ceil(rows.length / rowsPerPage))
        setPageNumber(1)
    }, [rows.length])

    const currentRfidsRows = useMemo(() => {
        return rows.slice(
            rowsPerPage * (pageNumber - 1),
            rowsPerPage * pageNumber
        )
    }, [rows, pageNumber])

    const handleChangePage = useCallback(
        (_event: React.ChangeEvent<unknown>, page: number) => {
            setPageNumber(page)
            setCurrentCardSelected(null)
        },
        []
    )

    const onClick = (item: RfidInfo) => {
        setRfidCardRuleId(item.id)
        setCurrentCardSelected(item)
        setViewMode("EDIT")
    }

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: { xs: "1rem", sm: "1.5rem", md: "2rem" },
                    justifyContent: "center",
                    width: "100%",
                    padding: { xs: "0.5rem", sm: "1rem" },
                    "& > *": {
                        flex: "1 1 17.5rem",
                        maxWidth: "21.875rem",
                        minWidth: "17.5rem",
                    },
                }}
            >
                {currentRfidsRows.map((item: RfidInfo, index) => (
                    <Box key={item.id || index}>
                        <Card
                            sx={{
                                width: "100%",
                                maxWidth: "18.75rem",
                                height: "16rem",
                                borderRadius: "1.25rem",
                                boxShadow: 9,
                            }}
                        >
                            <CardHeader
                                sx={{
                                    width: "100%",
                                    maxWidth: "18.75rem",
                                    height: "5.5rem",
                                    flexShrink: 0,
                                    background:
                                        item.status === RfidStatus.Active
                                            ? `linear-gradient(90deg, var(--Colors-green-hover, #00917B) 13.28%, var(--Colors-dark-green, #003936) 87%)`
                                            : `linear-gradient(90deg, #DDE1E6 0%, #343A3F 100%)`,
                                    color: "white",
                                    px: "1rem",
                                    pt: "0.5rem",
                                    borderRadius: "1.25rem 1.25rem 100% 100%",
                                    position: "relative",
                                    overflow: "hidden",
                                    "& .MuiCardHeader-avatar": {
                                        marginRight: "0.75rem",
                                    },
                                    "& .MuiCardHeader-action": {
                                        margin: 0,
                                        alignSelf: "center",
                                    },
                                    "& .MuiSvgIcon-root": {
                                        color: "white",
                                    },
                                }}
                                avatar={
                                    <Avatar
                                        alt="Profile Picture"
                                        sx={{
                                            bgcolor:
                                                item.status ===
                                                RfidStatus.Active
                                                    ? "secondary.main"
                                                    : "grey.700",
                                            width: "2.8rem",
                                            height: "2.8rem",
                                        }}
                                    >
                                        {item.cardHolderType == "DRIVER" ? (
                                            <img
                                                src={person}
                                                alt="icon-person"
                                                style={{
                                                    filter: "brightness(0) invert(1)",
                                                }}
                                            />
                                        ) : null}
                                        {item.cardHolderType == "VEHICLE" ? (
                                            <img
                                                src={carUtility}
                                                alt="icon-car-utility"
                                                style={{
                                                    filter: "brightness(0) invert(1)",
                                                }}
                                            />
                                        ) : null}
                                    </Avatar>
                                }
                                action={
                                    <StatusRfidCell
                                        rfid={item}
                                        sx={{
                                            mb: "1rem",
                                            px: "0.6rem",
                                            py: "0.9rem",
                                        }}
                                    />
                                }
                            />
                            <CardContent
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mt: "-2.5rem",
                                }}
                            >
                                <img
                                    alt="Active RFID card"
                                    src={
                                        item.status === RfidStatus.Active
                                            ? ActiveCardIcon
                                            : DeactiveCardIcon
                                    }
                                    style={{ zIndex: 1000, width: "6rem" }}
                                />
                                <Typography
                                    variant={"labelLarge"}
                                    sx={{ mt: "1rem", mb: "0.4rem" }}
                                >
                                    {item.cardHolder}
                                </Typography>
                                <Typography variant={"body"}>
                                    n°{item.rfidNumber}
                                </Typography>
                            </CardContent>
                            <CardActions>
                                {item.status === RfidStatus.Active ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "space-between",
                                            width: "100%",
                                            px: "1rem",
                                            mt: "-0.5rem",
                                        }}
                                    >
                                        <OreveButton
                                            data-cy="card__rfid--edit"
                                            variant="text"
                                            icon={RuleIcon}
                                            onClick={() => onClick(item)}
                                            removeMargin={true}
                                            gapSize="0.3em"
                                            sx={{
                                                textDecoration: "none",
                                                gap: "0",
                                                m: "0",
                                                width: "auto",
                                            }}
                                            value="Modifier les règles"
                                        />
                                        <OreveButton
                                            data-cy="card__rfid--disable"
                                            variant="text"
                                            icon={DoDisturbAltOutlinedIcon}
                                            removeMargin={true}
                                            gapSize="0.3em"
                                            onClick={() => {
                                                stateDisableCard()
                                                setCurrentCardSelected(item)
                                            }}
                                            sx={{
                                                color: "waiting.main",
                                                textDecoration: "none",
                                                m: "0",
                                                gap: "0",
                                            }}
                                            value="Désactiver"
                                        />
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            width: "100%",
                                            height: "100%",
                                            px: "0.5rem",
                                            mt: "-0.5rem",
                                        }}
                                    >
                                        <OreveButton
                                            data-cy="card__rfid--reactivate"
                                            variant="text"
                                            value="Réactiver"
                                            gapSize="0.3em"
                                            removeMargin={true}
                                            icon={ReplayIcon}
                                            onClick={() => {
                                                stateReactivateCard()
                                                setCurrentCardSelected(item)
                                            }}
                                            sx={{
                                                color: "success.main",
                                                textDecoration: "none",
                                                m: "0",
                                                gap: "0",
                                            }}
                                        />
                                    </Box>
                                )}
                            </CardActions>
                        </Card>
                    </Box>
                ))}
            </Box>
            <Pagination
                data-testid="Pagination"
                color="primary"
                count={numberOfPages}
                page={pageNumber}
                onChange={handleChangePage}
                renderItem={(item) => {
                    if (item.type === "previous") {
                        return (
                            <PaginationItem
                                {...item}
                                data-cy="cards__pagination--prev"
                            />
                        )
                    }
                    if (item.type === "next") {
                        return (
                            <PaginationItem
                                {...item}
                                data-cy="cards__pagination--next"
                            />
                        )
                    }
                    return <PaginationItem {...item} />
                }}
                sx={{
                    mx: "auto",
                }}
            />
        </>
    )
}
